import { Component, DoCheck, Input, OnInit, OnDestroy, OnChanges, SimpleChanges } from '@angular/core';
import { Alignment, QuestionSubType } from '@app/core/enums/questionnaire.enums';
import { InputMultiSelect } from '@app/shared/components/form-fields/input-multi-select/input-multi-select';
import { InputSelect } from '@app/shared/components/form-fields/input-select/input-select';
import { CommonModule } from '@angular/common';
import { FormsModule, FormControl, ReactiveFormsModule } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'app-choice-based',
  imports: [InputMultiSelect, InputSelect, CommonModule, FormsModule, ReactiveFormsModule],
  templateUrl: './choice-based.html',
  styleUrl: './choice-based.scss'
})
export class ChoiceBased implements OnInit, OnDestroy, OnChanges, DoCheck {
  @Input() question: any;
  @Input() data: any | null = null;
  @Input() page: 'theme' | 'preview' = 'preview';
  @Input() viewMode: 'mobile' | 'desktop' = 'desktop';
  questionSubType = QuestionSubType;
  alignment = Alignment;
  
  selectedOptions: any[] = [];
  othersTextMap: Map<string, string> = new Map(); // Store text for each Others option
  private othersOptionIds: Set<string> = new Set(); // Cache all Others option IDs
  private destroy$ = new Subject<void>();
  private lastQuestionSignature = '';
  
  // Form control for multi-select dropdown
  multiSelectControl = new FormControl([]);

  ngOnInit() {
    // Initialize othersOptionIds if question is available
    if (this.question) {
      this.othersOptionIds = this.findOthersOptionIds();
    }
    this.setupSubscriptions();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['question']) {
      this.refreshQuestionState(!changes['question'].firstChange);
    }
  }

  ngDoCheck(): void {
    const currentSignature = this.getQuestionSignature();
    if (currentSignature !== this.lastQuestionSignature) {
      this.refreshQuestionState(true);
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private setupSubscriptions() {
    // Cache all Others option IDs for faster lookups
    this.othersOptionIds = this.findOthersOptionIds();
    
    // Subscribe to multi-select changes (backup for reactive forms sync)
    // Primary handler is now the (change) event in template
    this.multiSelectControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(values => {
        // Only sync if values are different to avoid duplicate calls
        const currentValues = this.selectedOptions.map(o => o.text);
        const newValues = Array.isArray(values) ? values.map((v: any) => v.toString()) : [];
        if (JSON.stringify(currentValues.sort()) !== JSON.stringify(newValues.sort())) {
          this.onMultiSelectChange(values);
        }
      });
  }

  private resetControls() {
    // Reset form control
    this.multiSelectControl.setValue([], { emitEvent: false });
    
    // Clear selected options and others text
    this.selectedOptions = [];
    this.othersTextMap.clear();
  }

  private refreshQuestionState(resetSelection: boolean): void {
    if (resetSelection) {
      this.resetControls();
    }
    this.othersOptionIds = this.findOthersOptionIds();
    this.lastQuestionSignature = this.getQuestionSignature();
  }

  private getQuestionSignature(): string {
    const questionId = this.question?.questionId ?? '';
    const subtype = this.question?.questionSubType ?? '';
    const options = this.question?.questionOptions ?? [];
    const optionSignature = options
      .map((opt: any) => `${opt.optionId ?? ''}|${opt.order ?? ''}|${opt.text ?? ''}`)
      .join('||');
    return `${questionId}::${subtype}::${optionSignature}`;
  }

  getAlignmentClass(question: any): string {
    return question.questionSpecificOptions?.alignment === this.alignment.HORIZONTAL
      ? 'horizontal'
      : 'vertical';
  }
  
  getChoiceOptions(options: any[]): { label: string; value: string }[] {
    if (!options?.length) return [];
    // Include all options including "Others" in dropdown
    return options.map((opt) => ({
      label: opt.text,
      value: opt.text
    }));
  }

  // Find option by text value
  findOptionByText(text: string): any | null {
    if (!this.question?.questionOptions) return null;
    return this.question.questionOptions.find((opt: any) => opt.text === text) || null;
  }

  // Get filtered options (excluding Others) for display
  getDisplayOptions(): any[] {
    if (!this.question?.questionOptions) return [];
    return this.question.questionOptions.filter((opt: any) => !this.isOthersOption(opt));
  }

  // Get Others option if it exists
  getOthersOption(): any | null {
    if (!this.question?.questionOptions) return null;
    return this.question.questionOptions.find((opt: any) => this.isOthersOption(opt)) || null;
  }

  isOthersOption(option: any): boolean {
    return option?.text?.toLowerCase().trim() === 'others';
  }

  isOthersSelected(option: any): boolean {
    if (!option?.optionId) return false;
    return this.othersOptionIds.has(option.optionId) && 
           this.selectedOptions.some(o => o.optionId === option.optionId);
  }

  private findOthersOptionIds(): Set<string> {
    const ids = new Set<string>();
    if (!this.question?.questionOptions) return ids;
    this.question.questionOptions.forEach((opt: any) => {
      if (this.isOthersOption(opt)) {
        ids.add(opt.optionId || opt.order?.toString());
      }
    });
    return ids;
  }

  onChoiceClick(option: any, event: Event) {
    event.stopPropagation(); // Prevent parent click events
  
    const allowMultiple = this.question.questionSpecificOptions?.allowMultipleResponses;
    const optionId = option.optionId || option.order?.toString();
  
    if (allowMultiple) {
      // Checkbox style (multi-select)
      const index = this.selectedOptions.findIndex(o => 
        (o.optionId || o.order?.toString()) === optionId
      );
      if (index > -1) {
        // Deselect
        this.selectedOptions.splice(index, 1);
        // Clear others text if it was an Others option
        if (this.othersOptionIds.has(optionId)) {
          this.othersTextMap.delete(optionId);
        }
      } else {
        // Select
        this.selectedOptions.push(option);
      }
    } else {
      // Radio style (single-select)
      const previousOption = this.selectedOptions[0];
      if (previousOption && this.othersOptionIds.has(previousOption.optionId || previousOption.order?.toString())) {
        this.othersTextMap.delete(previousOption.optionId || previousOption.order?.toString());
      }
      this.selectedOptions = [option];
    }
  }

  onCheckboxChange(option: any, event: Event) {
    event.stopPropagation();
    this.onChoiceClick(option, event);
  }

  onRadioChange(option: any, event: Event) {
    event.stopPropagation();
    this.onChoiceClick(option, event);
  }
  
  isSelected(option: any): boolean {
    const optionId = option.optionId || option.order?.toString();
    return this.selectedOptions.some(o => 
      (o.optionId || o.order?.toString()) === optionId
    );
  }

  onOthersTextChange(option: any, text: string) {
    const optionId = option.optionId || option.order?.toString();
    this.othersTextMap.set(optionId, text);
  }

  getOthersText(option: any): string {
    const optionId = option.optionId || option.order?.toString();
    return this.othersTextMap.get(optionId) || '';
  }

  // Handle dropdown single select change
  onDropdownChange(selectedOption: any) {
    if (!selectedOption) {
      // Option was deselected
      const previousOption = this.selectedOptions[0];
      if (previousOption && this.othersOptionIds.has(previousOption.optionId || previousOption.order?.toString())) {
        this.othersTextMap.delete(previousOption.optionId || previousOption.order?.toString());
      }
      this.selectedOptions = [];
      return;
    }
    
    // selectedOption is a DropdownOption object with { label, value }
    const optionText = selectedOption.value || selectedOption.label;
    const option = this.findOptionByText(optionText);
    if (!option) return;

    // Single select - replace current selection
    const previousOption = this.selectedOptions[0];
    if (previousOption && this.othersOptionIds.has(previousOption.optionId || previousOption.order?.toString())) {
      this.othersTextMap.delete(previousOption.optionId || previousOption.order?.toString());
    }
    this.selectedOptions = [option];
  }

  // Handle dropdown multi-select change
  onMultiSelectChange(selectedValues: (string | number)[] | null) {
    if (!Array.isArray(selectedValues)) {
      this.selectedOptions = [];
      this.othersTextMap.clear();
      // Sync form control
      this.multiSelectControl.setValue([], { emitEvent: false });
      return;
    }

    // Get all selected options
    const newSelectedOptions: any[] = [];
    selectedValues.forEach(value => {
      const option = this.findOptionByText(value.toString());
      if (option) {
        newSelectedOptions.push(option);
      }
    });

    // Clear others text for options that are no longer selected
    const currentOptionIds = new Set(newSelectedOptions.map(o => o.optionId || o.order?.toString()));
    this.selectedOptions.forEach(opt => {
      const optId = opt.optionId || opt.order?.toString();
      if (!currentOptionIds.has(optId) && this.othersOptionIds.has(optId)) {
        this.othersTextMap.delete(optId);
      }
    });

    // Update selectedOptions - this triggers change detection
    this.selectedOptions = [...newSelectedOptions];
    
    // Sync form control value (without emitting to avoid circular updates)
    const formControlValues = selectedValues.map(v => v.toString());
    const currentControlValues = this.multiSelectControl.value || [];
    if (JSON.stringify(formControlValues.sort()) !== JSON.stringify(currentControlValues.sort())) {
      this.multiSelectControl.setValue(formControlValues as unknown as never[], { emitEvent: false });
    }
  }

  // Check if Others is selected in dropdown (by checking selectedOptions)
  isOthersSelectedFromDropdown(): boolean {
    const othersOption = this.getOthersOption();
    if (!othersOption) return false;
    const othersOptionId = othersOption.optionId || othersOption.order?.toString();
    if (!othersOptionId) return false;
    // Check if Others option is in selectedOptions by comparing optionId or order
    return this.selectedOptions.some(opt => {
      const optId = opt.optionId || opt.order?.toString();
      return optId === othersOptionId;
    });
  }
}
